import { PublicClientApplication } from "@azure/msal-browser";
import { LBtn, LModal } from "@components";
import { DropdownMenuOption, LDropdownMenu } from "@components/LDropdownMenu";
import { NavItems } from "@helpers/navigation";
import { useState } from "react";
import { FormProvider, useForm } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { useAppSelector } from "../../../app/hooks";
import { msalConfig } from "../../../authConfig";
import { SelectLanguage } from "./selectLanguage";
import { OrganizationStatus } from "@helpers/organizationHelper";
import { getEnumKeyByValue } from "@helpers/enumHelper";

export const AdminHeader = ({ nav, showLanguage }: AdminHeaderProps): JSX.Element => {
    const [logOutModalOpen, setLogOutModalOpen] = useState(false);
    const [loading, setLoading] = useState(false);

    const validateMethods = useForm();
    const account = useAppSelector((state) => state.account);
    const organization = useAppSelector((x) => x.organization);
    const organizationAssessments = useAppSelector((x) => x.organizationAssessments);
    const navigate = useNavigate();

    const toggleModal = (): void => {
        setLogOutModalOpen(!logOutModalOpen);
    };

    const msalInstance = new PublicClientApplication(msalConfig);

    const handleLogout = async (): Promise<void> => {
        setLoading(true);
        const logoutRequest = {
            account: msalInstance.getAllAccounts()[0]
        };

        await msalInstance.logoutRedirect(logoutRequest);

        setLoading(false);
    };

    const handleOnDocumentationClick = (): void => {
        navigate(NavItems.Documentation.SupportTraning.relativePath);
    };

    const actionOptions = [
        {
            optionName: "Log Out",
            disabled: false,
            children: (
                <LBtn
                    classes="has-text-primary-blue has-text-weight-bold"
                    variation="unstyled"
                    fullWidth
                    onClick={toggleModal}
                >
                    Log Out
                </LBtn>
            )
        }
    ] as DropdownMenuOption[];

    if (
        (organization.status as string) === getEnumKeyByValue(OrganizationStatus, OrganizationStatus.active) &&
        organizationAssessments.hasPurchases
    ) {
        actionOptions.push({
            optionName: "Support and training",
            disabled: false,
            children: (
                <LBtn
                    classes="has-text-primary-blue has-text-weight-bold"
                    variation="unstyled"
                    fullWidth
                    onClick={handleOnDocumentationClick}
                >
                    Support and training
                </LBtn>
            )
        });
    }

    return (
        <>
            <div>
                <div className="is-primary pt-2 px-5 pb-0 admin-header-top">
                    <div className="navbar-end">
                        <div className="is-flex is-flex-direction-column">
                            <div className="navbar-item is-color-light">
                                <FormProvider {...validateMethods}>
                                    <LDropdownMenu
                                        id="resultsOptions"
                                        iconFamily="caret"
                                        switchIconOnActive
                                        iconColor="#fff"
                                        label={`${account.claims.firstName} ${account.claims.lastName}`}
                                        labelProps={{ className: "has-text-white" }}
                                        options={actionOptions}
                                        type="button"
                                        theme="light"
                                        isRightAligned
                                        itemClasses="p-0"
                                    />
                                </FormProvider>
                            </div>
                            {showLanguage && <SelectLanguage />}
                        </div>
                    </div>
                </div>
                {nav}
            </div>
            {logOutModalOpen ? (
                <LModal
                    fullHeight={false}
                    width={"s"}
                    onClose={toggleModal}
                    onConfirm={handleLogout}
                    closeOnOverlay
                    hasCancelConfirmButtons
                    confirmText="Log Out"
                    confirmLoading={loading}
                >
                    <div className="is-flex is-flex-direction-column">
                        <p className="content has-text-centered has-text-weight-bold is-size-5 mt-4">
                            Are you sure you want to log out?
                        </p>
                    </div>
                </LModal>
            ) : null}
        </>
    );
};

interface AdminHeaderProps {
    nav?: JSX.Element;
    showLanguage?: boolean;
}
